import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import customerService, { type Customer } from '../services/customer.service';
import { formatCustomerOptionLabel } from '@/utils/customerOptionLabel';

/**
 * Hook to fetch all active customers (for dropdowns)
 */
export const useActiveCustomers = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['customers', 'active'],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const customers = await customerService.getActiveCustomers();
      return customers || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry failed requests
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

/** Ant Select options with Education student labels when linked. */
export function useActiveCustomerOptions(options?: { enabled?: boolean }) {
  const query = useActiveCustomers(options);
  const selectOptions = useMemo(
    () =>
      (query.data || []).map((c: Customer) => ({
        value: c.id,
        label: formatCustomerOptionLabel(c),
        customer: c,
      })),
    [query.data]
  );
  return { ...query, options: selectOptions };
}

/**
 * Hook to fetch all customers with filters
 */
export const useCustomers = (params?: {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: async () => {
      const response = await customerService.getAllCustomers(params);
      console.log('[useCustomers] Response:', response);
      // Handle different response structures
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry failed requests
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

/**
 * Hook to fetch a single customer by ID
 */
export const useCustomerById = (id: string) => {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => customerService.getCustomerById(id),
    enabled: !!id,
  });
};

